import { useEffect, useState } from 'react';
import { logger } from '../../../shared/utils/logger.js';
import type { EmployeeRoleRow, EmployeeRow, EmployeeWriteInput } from '../api/employees.api';

type EmployeeEditorModalProps = {
  open: boolean;
  initial?: EmployeeRow | null;
  rolesAvailable: EmployeeRoleRow[];
  rolesLoading?: boolean;
  submitting: boolean;
  errorMessage?: string;
  onClose: () => void;
  onSubmit: (input: EmployeeWriteInput) => void;
};

const MODULE = 'employees-roles-employee-editor';

const logEvent = (event: string, fields: Record<string, unknown> = {}): void => {
  logger.info(MODULE, event, { event, ...fields });
};

const titleFor = (initial?: EmployeeRow | null): string =>
  initial?.employeeUuid ? 'Edit employee' : 'Add employee';

const PHONE_PATTERN = /^(|(?:\+91[-\s]?)?[6-9]\d{9})$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEFAULT_GENDER = 'UNSPECIFIED';

const EmployeeEditorModal = ({
  open,
  initial,
  rolesAvailable,
  rolesLoading,
  submitting,
  errorMessage,
  onClose,
  onSubmit,
}: EmployeeEditorModalProps) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [roleUuid, setRoleUuid] = useState('');
  const [showInApp, setShowInApp] = useState(false);
  const [gender, setGender] = useState(DEFAULT_GENDER);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (open) {
      setName(initial?.name || '');
      setPhone(initial?.phone || '');
      setEmail(initial?.email || '');
      setRoleUuid(initial?.role?.roleUuid || '');
      setShowInApp(Boolean(initial?.showInApp));
      setGender(initial?.gender || DEFAULT_GENDER);
      setLocalError('');
      logEvent('employee_editor_open', {
        mode: initial?.employeeUuid ? 'edit' : 'create',
        employeeUuid: initial?.employeeUuid || null,
      });
    }
  }, [open, initial]);

  if (!open) return null;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedName = name.trim();
    const trimmedPhone = phone.trim();
    const trimmedEmail = email.trim();

    if (!trimmedName) {
      setLocalError('Name is required.');
      logEvent('employee_editor_validation_failed', { reason: 'empty_name' });
      return;
    }
    if (!roleUuid) {
      setLocalError('Role is required.');
      logEvent('employee_editor_validation_failed', { reason: 'no_role' });
      return;
    }
    if (trimmedPhone && !PHONE_PATTERN.test(trimmedPhone)) {
      setLocalError('Phone must be a valid Indian mobile number.');
      logEvent('employee_editor_validation_failed', { reason: 'bad_phone' });
      return;
    }
    if (trimmedEmail && !EMAIL_PATTERN.test(trimmedEmail)) {
      setLocalError('Email is not valid.');
      logEvent('employee_editor_validation_failed', { reason: 'bad_email' });
      return;
    }

    setLocalError('');
    logEvent('employee_editor_submit', {
      mode: initial?.employeeUuid ? 'edit' : 'create',
      employeeUuid: initial?.employeeUuid || null,
      roleUuid,
    });
    onSubmit({
      name: trimmedName,
      phone: trimmedPhone,
      email: trimmedEmail,
      roleUuid,
      showInApp,
      gender,
    });
  };

  const activeRoles = rolesAvailable.filter(
    (r) => r.status === 'ACTIVE' || r.roleUuid === initial?.role?.roleUuid,
  );

  return (
    <div className="role-editor-overlay" role="dialog" aria-modal="true" aria-label={titleFor(initial)}>
      <form className="role-editor" onSubmit={handleSubmit}>
        <h3 className="role-editor__title">{titleFor(initial)}</h3>

        <label className="role-editor__field">
          <span className="role-editor__label">Name</span>
          <input
            className="role-editor__input"
            type="text"
            maxLength={100}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            disabled={submitting}
            required
          />
        </label>

        <label className="role-editor__field">
          <span className="role-editor__label">Role</span>
          <select
            className="role-editor__input"
            value={roleUuid}
            onChange={(e) => setRoleUuid(e.target.value)}
            disabled={submitting || rolesLoading}
            required
          >
            <option value="" disabled>
              {rolesLoading ? 'Loading roles…' : 'Select a role'}
            </option>
            {activeRoles.map((role) => (
              <option key={role.roleUuid} value={role.roleUuid}>
                {role.name}{role.status !== 'ACTIVE' ? ' (inactive)' : ''}
              </option>
            ))}
          </select>
        </label>

        <label className="role-editor__field">
          <span className="role-editor__label">Phone (optional)</span>
          <input
            className="role-editor__input"
            type="tel"
            maxLength={20}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={submitting}
            placeholder="9XXXXXXXXX"
          />
        </label>

        <label className="role-editor__field">
          <span className="role-editor__label">Email (optional)</span>
          <input
            className="role-editor__input"
            type="email"
            maxLength={255}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
          />
        </label>

        <label className="role-editor__field">
          <span className="role-editor__label">Gender</span>
          <select
            className="role-editor__input"
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            disabled={submitting}
          >
            <option value="UNSPECIFIED">Prefer not to say</option>
            <option value="MALE">Male</option>
            <option value="FEMALE">Female</option>
          </select>
        </label>

        <div className="role-editor__field">
          <span className="role-editor__label">Show in customer app</span>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              color: 'var(--text-secondary, #a1a1aa)',
            }}
          >
            <input
              type="checkbox"
              checked={showInApp}
              onChange={(e) => setShowInApp(e.target.checked)}
              disabled={submitting}
            />
            <span>
              {showInApp
                ? 'This employee can appear in the mobile app when they are active.'
                : 'Keep this employee hidden in the mobile app even if they are active.'}
            </span>
          </label>
        </div>

        {(localError || errorMessage) && (
          <p className="role-editor__error" role="alert">
            {localError || errorMessage}
          </p>
        )}

        <div className="role-editor__actions">
          <button
            type="button"
            className="role-editor__cancel"
            onClick={() => {
              logEvent('employee_editor_cancel', { employeeUuid: initial?.employeeUuid || null });
              onClose();
            }}
            disabled={submitting}
          >
            Cancel
          </button>
          <button type="submit" className="role-editor__submit" disabled={submitting}>
            {submitting ? 'Saving…' : initial?.employeeUuid ? 'Save changes' : 'Create employee'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EmployeeEditorModal;
